import { useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Card } from '@novaquantis/design-system';
import toast from 'react-hot-toast';
import BrandMark from '../../components/BrandMark';
import OnboardingBackChevron from '../../components/onboarding/OnboardingBackChevron';
import { hasActivePortfolio } from '../../hooks/useRetailAccounts';
import { useRetailAccounts as useRetailAccountsQuery } from '../../queries/retailAccounts';
import {
  ONBOARDING_STEPS,
  TERMINAL_STEP,
  isOnboardingStepKey,
  type OnboardingStepKey,
} from '../../onboarding/onboardingSteps';
import { useOnboardingStatus } from '../../onboarding/useOnboardingStatus';
import OnboardingTimeline from './OnboardingTimeline';
import OnboardingProgress from './OnboardingProgress';
import WelcomeStep from './steps/WelcomeStep';
import AlpacaSignupStep from './steps/AlpacaSignupStep';
import AlpacaKycStep from './steps/AlpacaKycStep';
import AlpacaApiKeysStep from './steps/AlpacaApiKeysStep';
import ConnectAlpacaStep from './steps/ConnectAlpacaStep';

// Shared card chrome: onboarding/login glass card fill + soft shadow + glow inset.
const CARD_CLASS =
  'rounded-xl border border-[color:var(--border-slate)] bg-[color:var(--nq-surface-card-2)] shadow-[var(--shadow-card-soft),var(--inset-card-glow)]';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { status, loading, error, completeStep, visitStep } = useOnboardingStatus();
  const [continuing, setContinuing] = useState(false);
  const visitInFlight = useRef(false);

  const {
    data: accounts = [],
    isLoading: accountsLoading,
    refetch: refetchAccounts,
  } = useRetailAccountsQuery();

  if (loading || accountsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center box-border min-h-[calc(100vh-var(--app-header-height)-48px)] bg-transparent px-5 pb-8 pt-6 text-foreground">
        <p className="text-center text-sm text-muted-foreground">Loading onboarding…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center box-border min-h-[calc(100vh-var(--app-header-height)-48px)] bg-transparent px-5 pb-8 pt-6 text-foreground">
        <Card className={`${CARD_CLASS} mx-auto max-w-[480px] px-6 py-5`}>
          <p className="text-center text-sm text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  if (status.isComplete && hasActivePortfolio(accounts)) {
    return <Navigate to="/portfolio" replace />;
  }

  const needsAccountActivation =
    status.isComplete && !hasActivePortfolio(accounts);

  const currentStep = status.currentStep as OnboardingStepKey;
  const displayStep: OnboardingStepKey = needsAccountActivation
    ? 'connect_account'
    : isOnboardingStepKey(currentStep)
      ? currentStep
      : 'connect_account';
  const stepIndex = ONBOARDING_STEPS.indexOf(displayStep);
  const showBack = !needsAccountActivation && stepIndex > 0;

  const handleVisitStep = async (step: OnboardingStepKey) => {
    if (needsAccountActivation || step === displayStep || continuing || visitInFlight.current) {
      return;
    }
    visitInFlight.current = true;
    setContinuing(true);
    try {
      const { synced } = await visitStep(step);
      if (!synced) {
        toast('Showing previous step — restart app-bff and ledger if this keeps happening.', {
          icon: '↩',
        });
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not go to that step');
    } finally {
      visitInFlight.current = false;
      setContinuing(false);
    }
  };

  const handleBack = () => {
    if (stepIndex <= 0) {
      return;
    }
    handleVisitStep(ONBOARDING_STEPS[stepIndex - 1]);
  };

  const handleComplete = async (step: OnboardingStepKey) => {
    setContinuing(true);
    try {
      const next = await completeStep(step);
      if (next.isComplete && hasActivePortfolio(accounts)) {
        toast.success('Onboarding complete');
        navigate('/portfolio', { replace: true });
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Could not save progress',
      );
    } finally {
      setContinuing(false);
    }
  };

  const handleLinked = async () => {
    setContinuing(true);
    try {
      const { data: nextAccountsData } = await refetchAccounts();
      const nextAccounts = nextAccountsData ?? [];
      if (!hasActivePortfolio(nextAccounts)) {
        toast.error('Activate at least one account to continue');
        return;
      }

      if (status.isComplete) {
        toast.success('Account ready');
        navigate('/portfolio', { replace: true });
        return;
      }

      const next = await completeStep('connect_account');
      toast.success('Alpaca account connected');
      if (next.isComplete) {
        navigate('/portfolio', { replace: true });
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Could not finish onboarding',
      );
    } finally {
      setContinuing(false);
    }
  };

  const renderStep = () => {
    switch (displayStep) {
      case 'welcome':
        return (
          <WelcomeStep
            onContinue={() => handleComplete('welcome')}
            continuing={continuing}
          />
        );
      case 'alpaca_signup':
        return (
          <AlpacaSignupStep
            onContinue={() => handleComplete('alpaca_signup')}
            continuing={continuing}
          />
        );
      case 'alpaca_kyc':
        return (
          <AlpacaKycStep
            onContinue={() => handleComplete('alpaca_kyc')}
            continuing={continuing}
          />
        );
      case 'alpaca_api_keys':
        return (
          <AlpacaApiKeysStep
            onContinue={() => handleComplete('alpaca_api_keys')}
            continuing={continuing}
          />
        );
      case 'connect_account':
        return (
          <ConnectAlpacaStep
            onLinked={handleLinked}
            linking={continuing}
            reactivation={needsAccountActivation}
            onAccountsChange={() => { void refetchAccounts(); }}
          />
        );
      default:
        return (
          <div>
            <h1 className="m-0 mb-2.5 font-display text-2xl font-bold leading-tight tracking-tight text-secondary [text-shadow:var(--glow-gold-text)]">
              Something went wrong
            </h1>
            <p className="m-0 mb-3 text-sm leading-normal text-[color:var(--text-secondary)]">
              Unknown step ({status.currentStep}). Please contact support.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="flex-1 box-border min-h-[calc(100vh-var(--app-header-height)-48px)] bg-transparent px-5 pb-8 pt-6 text-foreground">
      <div
        className={`mx-auto grid max-w-[var(--content-max,1140px)] items-start gap-5 ${
          showBack
            ? 'md:grid-cols-[minmax(260px,320px)_40px_minmax(0,1fr)] md:gap-x-2 md:gap-y-5'
            : 'md:grid-cols-[minmax(260px,320px)_1fr]'
        }`}
      >
        <aside
          className={`${CARD_CLASS} order-1 p-5 md:col-start-1 md:row-start-1`}
        >
          <div className="mb-4">
            <BrandMark wordmarkSize={18} />
          </div>
          <h2 className="m-0 mb-3 font-display text-sm font-bold uppercase tracking-wide text-secondary">
            Getting started
          </h2>
          <OnboardingProgress completedSteps={status.completedSteps} />
          <OnboardingTimeline
            currentStep={needsAccountActivation ? TERMINAL_STEP : status.currentStep}
            completedSteps={status.completedSteps}
            onVisitStep={needsAccountActivation ? undefined : handleVisitStep}
          />
        </aside>
        {showBack && (
          <OnboardingBackChevron
            onClick={handleBack}
            disabled={continuing}
            className="order-2 mb-1.5 justify-self-start md:col-start-2 md:row-start-1 md:mb-0 md:mt-[17px] md:justify-self-center"
          />
        )}
        <section
          className={`${CARD_CLASS} order-3 px-6 py-5 ${
            showBack ? 'md:col-start-3 md:row-start-1' : ''
          }`}
        >
          {renderStep()}
        </section>
      </div>
    </div>
  );
};

export default OnboardingPage;
